import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BasicFilters } from '../model/filters/basic-filters';
import { Observable } from 'rxjs';
import { HttpUtils } from '../shared/http.utils';
import { TenantsResponse } from '../model/responses/tenants-response';
import { TenantPersistRequest } from '../model/requests/tenant-persist-request';
import { DeviceFilters } from "../model/filters/device-filters";
import { AssetFilters } from "../model/filters/asset-filters";
import { DevicesResponse } from "../model/responses/devices-response";
import { AssetsResponse } from "../model/responses/assets-response";
import { EntityMeasurementFilters } from "../model/filters/entity-measurement-filters";
import {EntityFilters} from "../model/filters/entity-filters";

@Injectable({
  providedIn: 'root'
})
export class TenantsService {

  private readonly tenantsURL: string;

  constructor(private http: HttpClient) {
    this.tenantsURL = '/catalog/tenants';
  }

  public getTenants(offset?: number, limit?: number, filters?: BasicFilters): Observable<TenantsResponse> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const body = (filters != null) ? filters : new BasicFilters();
    return this.http.post<TenantsResponse>(this.tenantsURL, body, options);
  }

  public getTenant(tenantUuid: string): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}`;
    return this.http.get<any>(url);
  }

  public createTenant(creationData: TenantPersistRequest): Observable<any> {
    const url = `${this.tenantsURL}/create`;
    return this.http.post<any>(url, creationData);
  }

  public editTenant(editionData: TenantPersistRequest): Observable<any> {
    const url = `${this.tenantsURL}/edit`;
    return this.http.post<any>(url, editionData);
  }

  public deleteTenant(tenantUuid: string): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public deleteTenants(uuids: string[]): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/delete`;
    return this.http.post<any>(url, uuids, {observe: 'response'});
  }

  public getTenantSummaries(): Observable<any> {
    const url = `${this.tenantsURL}/summary`;
    return this.http.get<any>(url);
  }


  public getTenantOrganizations(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/organizations`;
    return this.http.get<any>(url, options);
  }

  public getTenantOrganizationSummaries(tenantUuid: string): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/organizations/summary`;
    return this.http.get<any>(url);
  }


  public getTenantDecoders(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/decoders`;
    return this.http.get<any>(url, options);
  }

  public getTenantDecoder(tenantUuid: string, name: string): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/decoders/${name}`;
    return this.http.get<any>(url);
  }

  public deleteTenantDecoder(tenantUuid: string, name: string): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/decoders/${name}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }


  public getTenantEncoders(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/encoders`;
    return this.http.get<any>(url, options);
  }

  public getTenantEncoder(tenantUuid: string, name: string): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/encoders/${name}`;
    return this.http.get<any>(url);
  }

  public deleteTenantEncoder(tenantUuid: string, name: string): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/encoders/${name}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }


  public getTenantAssetRepresentations(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/asset_representations`;
    return this.http.get<any>(url, options);
  }

  public getTenantAssetRepresentation(tenantUuid, name): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/asset_representations/${name}`;
    return this.http.get<any>(url);
  }

  public deleteTenantAssetRepresentation(tenantUuid: string, name: string): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/asset_representations/${name}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public getTenantDeviceCategories(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/device_categories`;
    return this.http.get<any>(url, options);
  }

  public getTenantDeviceCategory(tenantUuid: string, name: string): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/device_categories/${name}`;
    return this.http.get<any>(url);
  }

  public deleteTenantDeviceCategory(tenantUuid: string, name: string): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/device_categories/${name}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public getTenantMeasurementTypes(tenantUuid: string, offset?: number, limit?: number) {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/measurement_types`;
    return this.http.get<any>(url, options);
  }

  public getTenantMeasurementType(tenantUuid: string, sensor: string): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/measurement_types/${sensor}`;
    return this.http.get<any>(url);
  }

  public deleteTenantMeasurementType(tenantUuid: string, sensor: string): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/measurement_types/${sensor}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }


  public getTenantEntityMeasurements(tenantUuid: string, offset?: number, limit?: number, filters?: EntityMeasurementFilters): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/entity_measurements`;
    const body = (filters != null) ? filters : new EntityMeasurementFilters();
    return this.http.post<any>(url, body, options);
  }

  public getTenantEntitiesFromMeasurement(tenantUuid: string, offset?: number, limit?: number, filters?: EntityFilters): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/entities_from_measurement`;
    const body = (filters != null) ? filters : new EntityFilters();
    return this.http.post<any>(url, body, options);
  }


  public getTenantApplicationSubscription(tenantUuid: any, name: any): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/application_subscriptions/${name}`;
    return this.http.get<any>(url);
  }

  public deleteTenantApplicationSubscription(tenantUuid: any, name: any): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/application_subscriptions/${name}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public getTenantTriggerRules(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/trigger_rules`;
    return this.http.get<any>(url, options);
  }

  public getTenantTriggerRule(tenantUuid: string, organizationUuid: string, name: string): Observable<any> {
    let url = `${this.tenantsURL}/${tenantUuid}/trigger_rules/${name}`;
    if (organizationUuid != null && organizationUuid !== '') { url += `?organizationUuid=${organizationUuid}`; }
    return this.http.get<any>(url);
  }

  public deleteTenantTriggerRule(tenantUuid: string, organizationUuid: string, name: string): Observable<HttpResponse<any>> {
    let url = `${this.tenantsURL}/${tenantUuid}/trigger_rules/${name}`;
    if (organizationUuid != null && organizationUuid !== '') { url += `?organizationUuid=${organizationUuid}`; }
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public getTenantCepRule(tenantUuid: string, organizationUuid: string, name: string): Observable<any> {
    let url = `${this.tenantsURL}/${tenantUuid}/cep_rules/${name}`;
    if (organizationUuid != null && organizationUuid !== '') { url += `?organizationUuid=${organizationUuid}`; }
    return this.http.get<any>(url);
  }

  public getExistsTenantCepRule(tenantUuid: string, organizationUuid: string, name: string): Observable<any> {
    let url = `${this.tenantsURL}/${tenantUuid}/cep_rules/exists/${name}`;
    if (organizationUuid != null && organizationUuid !== '') { url += `?organizationUuid=${organizationUuid}`; }
    return this.http.get<any>(url);
  }

  public deleteTenantCepRule(tenantUuid: string, organizationUuid: string, nameComplexRule: string): Observable<HttpResponse<any>> {
    let url = `${this.tenantsURL}/${tenantUuid}/cep_rules/${nameComplexRule}`;
    if (organizationUuid != null && organizationUuid !== '') { url += `?organizationUuid=${organizationUuid}`; }
    return this.http.delete<any>(url, {observe: 'response'});
  }

  public getTenantPropertyTypes(tenantUuid: string, offset?: number, limit?: number): Observable<any> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/property_types`;
    return this.http.get<any>(url, options);
  }

  public getTenantDevices(tenantUuid: string, offset?: number, limit?: number, filters?: DeviceFilters): Observable<DevicesResponse> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/devices`;
    let body = (filters != null) ? filters : new DeviceFilters();
    return this.http.post<any>(url, body, options);
  }

  public getTenantDevicesSummaries(tenantUuid: string) {
    let url = `${this.tenantsURL}/${tenantUuid}/summaries/devices`;
    return this.http.get<any>(url);
  }

  public getTenantAssets(tenantUuid: string, offset?: number, limit?: number, filters?: AssetFilters): Observable<AssetsResponse> {
    const options = HttpUtils.buildOptions(HttpUtils.buildListParams(offset, limit));
    const url = `${this.tenantsURL}/${tenantUuid}/assets`;
    let body = (filters != null) ? filters : new AssetFilters();
    return this.http.post<any>(url, body, options);
  }

  public getTenantAssetsSummaries(tenantUuid: string) {
    let url = `${this.tenantsURL}/${tenantUuid}/summaries/assets`;
    return this.http.get<any>(url);
  }

  public getTenantApplicationNotification(tenantUuid: any, uuid: any): Observable<any> {
    const url = `${this.tenantsURL}/${tenantUuid}/application_notifications/${uuid}`;
    return this.http.get<any>(url);
  }

  public deleteTenantApplicationNotification(tenantUuid: any, uuid: any): Observable<HttpResponse<any>> {
    const url = `${this.tenantsURL}/${tenantUuid}/application_notifications/${uuid}`;
    return this.http.delete<any>(url, {observe: 'response'});
  }

}
